import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import MaterialIcon from "./MaterialIcon";

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
  /** Drawn before the label, in the list and on the closed control. */
  icon?: ReactNode;
  /** A rule above this option: the start of a group. */
  divider?: boolean;
}

/**
 * A select drawn by the page, so its list looks like the rest of the UI
 * and its options can carry an icon. Arrow keys move, Enter and Space
 * pick, Escape and a click outside close.
 */
export default function Dropdown<T extends string>({
  value,
  options,
  onChange,
  label,
  className = "",
}: {
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  /** What the control is for, for assistive tech. */
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(0);
  // Where the list goes: fixed to the viewport under the control, so a
  // scrolling or clipping container around the control can't cut it off.
  // Where the list opens: under the control, or over it when there's more
  // room above (a control near the bottom of a phone's screen).
  const [at, setAt] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const control = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  const selected = Math.max(0, options.findIndex((o) => o.value === value));
  const current = options[selected];

  // The list opens on the chosen option.
  const show = (focus = selected) => {
    const rect = control.current?.getBoundingClientRect();
    if (rect) {
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const LIST = 320;
      setAt(
        below >= Math.min(LIST, 200) || below >= above
          ? { left: rect.left, width: rect.width, top: rect.bottom + 4, maxHeight: Math.min(LIST, below) }
          : { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(LIST, above) },
      );
    }
    setFocused(focus);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!root.current?.contains(target) && !list.current?.contains(target)) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const pick = (option: DropdownOption<T>) => {
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const move = (to: number) => {
      e.preventDefault();
      show(Math.min(options.length - 1, Math.max(0, to)));
    };
    if (e.key === "ArrowDown") move(open ? focused + 1 : selected);
    else if (e.key === "ArrowUp") move(open ? focused - 1 : selected);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(options.length - 1);
    else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (open) pick(options[focused]);
      else show();
    }
  };

  return (
    <div ref={root} className={`dropdown${open ? " open" : ""}${className ? ` ${className}` : ""}`}>
      <button
        ref={control}
        type="button"
        className={`select dropdown-control${open ? " open" : ""}`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        // WebKit doesn't focus a clicked button, and the keys go to it.
        onClick={(e) => {
          e.currentTarget.focus();
          if (open) setOpen(false);
          else show();
        }}
        onKeyDown={onKeyDown}
      >
        {current.icon && <span className="dropdown-icon">{current.icon}</span>}
        <span className="dropdown-label">{current.label}</span>
        <MaterialIcon name="expand_more" />
      </button>
      {/* At the document root: a transformed or scrolling ancestor would
          otherwise clip or misplace the list. */}
      {open &&
        at &&
        createPortal(
        <ul
          id={id}
          ref={list}
          role="listbox"
          aria-label={label}
          className="dropdown-list"
          style={{
            left: Math.min(at.left, window.innerWidth - 16 - Math.max(at.width, 160)),
            top: at.top,
            bottom: at.bottom,
            minWidth: at.width,
            maxHeight: at.maxHeight,
          }}
        >
          {options.map((option, i) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`dropdown-option${option.value === value ? " selected" : ""}${i === focused ? " focused" : ""}${option.divider ? " divider" : ""}`}
              onPointerEnter={() => setFocused(i)}
              onClick={() => pick(option)}
            >
              {option.icon && <span className="dropdown-icon">{option.icon}</span>}
              <span className="dropdown-label">{option.label}</span>
              {option.value === value && <MaterialIcon name="check" />}
            </li>
          ))}
        </ul>,
          document.body,
        )}
    </div>
  );
}
